import { createContext, ReactNode, useCallback, useMemo, useState } from 'react';
import { randomId } from '@mantine/hooks';
import {
  Button,
  CSSObject,
  Divider,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useIntlLight } from '../../../i18n';

interface Options {
  onClose?: () => void;
  onConfirm?: () => void;
  secureConfirm?: boolean;
  title?: ReactNode;
  size?: 'lg' | 'xl' | '' | string;
  isFullScreen?: boolean;
  preventDefaultClosing?: boolean;
}

export type IGlobalModalContext = {
  open?: (content: ReactNode, options?: Options) => void;
  close?: () => void;
};

interface GlobalModalProviderProps {
  children?: ReactNode;
}

export const GlobalModalContext = createContext<IGlobalModalContext | null>(null);
GlobalModalContext.displayName = 'GlobalModal';

export default function GlobalModalProvider({ children }: GlobalModalProviderProps) {
  const [content, setContent] = useState<ReactNode | string>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<Options>();
  const [hash, setHash] = useState<string>();
  const [hashConfirm, setHashConfirm] = useState<string>();
  const { t } = useIntlLight();

  const modalStyles = {
    xxl: {
      modal: {
        height: '100%',
        width: '80vw',
      } as CSSObject,
      body: {
        height: '80vh',
        width: '100%',
        overflowY: 'auto',
      } as CSSObject,
    },
  };

  const open = useCallback((content: ReactNode, options?: Options) => {
    setIsOpen(true);
    setContent(content);
    setOptions({ ...options });

    if (options?.secureConfirm) {
      const tmpHash = randomId().replace('mantine-', '');
      setHash(tmpHash);
    }
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const contextValue = useMemo(() => {
    return { open, close };
  }, [open, close]);

  return (
    <GlobalModalContext.Provider value={contextValue}>
      {children}
      <Modal
        fullScreen={options?.isFullScreen}
        withCloseButton={!options?.preventDefaultClosing}
        closeOnEscape={!options?.preventDefaultClosing}
        closeOnClickOutside={!options?.preventDefaultClosing}
        size={options?.size !== 'xxl' ? options?.size : undefined}
        opened={isOpen}
        onClose={() => {
          options?.onClose?.();
          close();
        }}
        zIndex={1000}
        styles={options?.size === 'xxl' ? modalStyles.xxl : {}}
        centered
        title={options?.title && <Title order={5}>{options?.title}</Title>}
      >
        {content}
        {options?.onConfirm && (
          <>
            {options.secureConfirm && (
              <>
                <Divider my="lg" />
                <Text>{t('app.secureConfirm')}</Text>
                <Stack my="lg">
                  <Text weight="bold">{hash}</Text>
                  <TextInput onChange={e => setHashConfirm(e.target.value)} />
                </Stack>
              </>
            )}
            <Group position="right" mt="xl">
              <Button
                color="red"
                type="button"
                onClick={() => {
                  options?.onClose?.();
                  close();
                }}
              >
                {t('app.cancel')}
              </Button>
              <Button
                color="blue"
                type="submit"
                onClick={() => {
                  options?.onConfirm?.();
                  close();
                }}
                disabled={options?.secureConfirm ? !(hash === hashConfirm) : false}
              >
                {t('app.confirm')}
              </Button>
            </Group>
          </>
        )}
      </Modal>
    </GlobalModalContext.Provider>
  );
}
